/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { useGetAllProductsInfiniteQuery } from "@/api/AppApi/services/product";
import { useEffect, useState } from "react";
import Separator from "../../../lib/Separator";
import ProductCarousel from "@/app/components/common-builder/product/product-carousel/ProductCarousel";
import ProductGrid from "@/app/components/common-builder/product/product-grids/ProductGrid";
import { useIntersectionObserver as useInView } from "@/hooks/useIntersectionObserver";
import { Spinner } from "@/utils/bootstrap";
import BundleAssociatedProduct from "./bundle-associated-product";
import AssociatedProductsSkeleton from "./AssociatedProductsSkeleton";
import { useTenantText } from "@/hooks/localization/useTenantText";

export enum AssociatedProductsTypes {
  upsell,
  related,
  similar,
}

interface Props {
  type: AssociatedProductsTypes;
  id: number | string | number[];
}

const AssociatedProducts: React.FC<Props> = ({ type, id }) => {
  const [isLastProductInSlider, setIsLastProductInSlider] = useState(false);
  const tenantText = useTenantText();
  const { data: companyData } = useGetCompanyQuery();
  const theme = companyData?.result?.theme;
  const components = theme?.components;

  const size = components?.product?.product_box_style ?? Size.small;
  const productSize = size;

  const ids = Array.isArray(id) ? id.join(",") : id;

  const productSettings = {
    [AssociatedProductsTypes.upsell]: {
      title: components?.product?.upsell_products_title,
      asGrid: components?.product?.upsell_products_asgrid,
      view: components?.product?.show_upsell_products,
      params: { upsell_products: ids },
    },
    [AssociatedProductsTypes.related]: {
      title: components?.product?.related_products_title,
      asGrid: components?.product?.related_products_asgrid,
      view: components?.product?.show_related_products,
      params: { related_products: ids },
    },
    [AssociatedProductsTypes.similar]: {
      title: components?.product?.similar_products_title,
      asGrid: components?.product?.similar_products_asgrid,
      view: components?.product?.show_similar_products,
      params: { similar_products: ids },
    },
  };

  const { title: rawTitle, asGrid, view, params } = productSettings[type];
  // Merchant copy: may arrive as a plain string or an {en,ar} JSON map.
  const title = tenantText(rawTitle);
  // Recommendation carousels reuse the /products list endpoint — tell the backend
  // NOT to fire the ViewContentList event for these secondary lists.
  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    data,
    isInitialLoading,
  } = useGetAllProductsInfiniteQuery({
    params: { ...params, without_content_list: 1 },
    enabled: view,
  });

  const products = data?.pages?.flatMap((page) => page.result.data) || [];

  const fetchMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const [ref, isInView] = useInView();

  useEffect(() => {
    if (isInView) {
      fetchMore();
    }
  }, [isInView]);

  useEffect(() => {
    if (isLastProductInSlider) {
      fetchMore();
      setIsLastProductInSlider(false);
    }
  }, [isLastProductInSlider]);

  // Safety timeout: if the query stays in initial-loading for too long on a
  // weak device (e.g. because `enabled` hasn't flipped yet or the network is
  // very slow), stop showing the skeleton after 8 seconds.  This prevents the
  // "stuck skeleton until reload" bug.
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!isInitialLoading) return;
    const id = setTimeout(() => setTimedOut(true), 8000);
    return () => clearTimeout(id);
  }, [isInitialLoading]);

  const bundles = products.filter((product) => product.type === "bundle");
  const otherProducts = products.filter((product) => product.type !== "bundle");

  // Use `isInitialLoading` instead of `isLoading`:
  // - `isLoading` is true even when the query is *disabled* (waiting for `view`
  //   to become truthy) — this shows a skeleton for a query that hasn't started.
  // - `isInitialLoading` is only true when the query is *enabled AND fetching
  //   for the first time*, which is the actual "loading from network" state.
  // Also bail out if the safety timeout fired.
  if (isInitialLoading && view && !timedOut)
    return <AssociatedProductsSkeleton />;
  if (!view || products.length === 0) return null;

  return (
    <div>
      <Separator startLabel={title} marginBottom={16} />
      {asGrid ? (
        <>
          <ProductGrid
            products={{
              data: otherProducts,
              configurations: { style_type: size },
            }}
            theme={theme!}
          />
          <div ref={ref} className="w-100 d-flex justify-content-center mt-3">
            {hasNextPage && <Spinner />}
          </div>
        </>
      ) : (
        <ProductCarousel
          setIslastProduct={setIsLastProductInSlider}
          products={{
            data: otherProducts,
            configurations: { style_type: productSize },
          }}
          theme={theme!}
        />
      )}
      <div className=" d-flex flex-column gap-4">
        {bundles?.map((bundle) => (
          <BundleAssociatedProduct key={bundle.id} slug={bundle.slug} />
        ))}
      </div>
    </div>
  );
};

export default AssociatedProducts;
